/**
 * Navigation Configuration
 *
 * Centralized navigation configuration for the journey-web application.
 * Defines sidebar navigation items, sections, and footer items.
 */

import {
  Archive,
  BadgeCheck,
  Briefcase,
  ClipboardCheck,
  ListChecks,
  CreditCard,
  GraduationCap,
  Heart,
  Home,
  Plane,
  Hourglass,
  Settings,
  User,
  Users,
} from '@tonnie/assets';

import {
  DEFAULT_SETTINGS_TAB,
  SETTINGS_PATH,
  settingsTabFromSearch,
  settingsTabHref,
} from '@/features/secondary/settings/settings.types';

import type { INavItem } from '../types/navigation.types';

// ═══════════════════════════════════════════════════════════════════════
// NAVIGATION ITEMS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Home navigation item
 */
export const homeNavItem: INavItem = {
  icon: Home,
  label: 'Home',
  href: '/dashboard',
};

/**
 * The journey's heroes — the five a relocation journey is made of. Each one
 * opens on the journey and its plan (a hero the plan does not include shows
 * its lock).
 */
export const heroNavItems: INavItem[] = [
  { icon: ClipboardCheck, label: 'CheckUp', href: '/checkup', featureType: 'checkup' },
  { icon: Plane, label: 'TelePortal', href: '/teleportal', featureType: 'teleportal' },
  { icon: GraduationCap, label: 'QultureUp', href: '/qultureup', featureType: 'qultureup' },
  { icon: Heart, label: 'Care', href: '/care', featureType: 'care' },
  { icon: BadgeCheck, label: 'Passport', href: '/passport', featureType: 'passport' },
];

/**
 * The heroes of the second line (owner, 19 Oct): used on their own, without a
 * structured journey — after the five, before the person's own pages. InOut is
 * person-level (shown while INOUT_ENABLED is on); TestIt is the citizen's
 * reusable-test catalog (BL-214).
 */
export const standaloneHeroNavItems: INavItem[] = [
  { icon: Hourglass, label: 'InOut', href: '/inout' },
  { icon: ListChecks, label: 'TestIt', href: '/testit', translationKey: 'nav.testit' },
];

/**
 * Secondary navigation items - the accessory pages, each behind its own flag
 */
export const secondaryNavItems: INavItem[] = [
  { icon: Users, label: 'Family', href: '/family' },
  { icon: Briefcase, label: 'Career', href: '/career' },
];

/**
 * Footer navigation items - the person's account, top to bottom (owner,
 * 19 Oct): Billing, Vault, Profile, then Settings.
 */
export const footerNavItems: INavItem[] = [
  { icon: CreditCard, label: 'Billing', href: '/billing' },
  { icon: Archive, label: 'Vault', href: '/vault' },
  { icon: User, label: 'Profile', href: settingsTabHref('profile'), translationKey: 'nav.profile' },
  {
    icon: Settings,
    label: 'Settings',
    href: settingsTabHref('security'),
    translationKey: 'nav.settings',
  },
];

// ═══════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

/** Whether `href` is the current path or one of its nested routes. */
function isRouteActive(currentPath: string, href: string): boolean {
  // Exact match
  if (currentPath === href) return true;

  // Dashboard special case
  if (currentPath === '/' && href === '/dashboard') return true;

  // Prefix match for nested routes (e.g., /checkup/assessment)
  if (href !== '/dashboard' && currentPath.startsWith(href + '/')) return true;

  return false;
}

/**
 * Whether a nav item is the current page, from the URL alone.
 *
 * Two items open Settings: Profile (its default tab) and Settings (the other
 * tabs). On /settings the tab in the URL decides — it used to be which item
 * was last CLICKED, so a link, a reload or a tab change lit the wrong one.
 */
export function isNavItemActive(currentPath: string, currentSearch: string, href: string): boolean {
  const [hrefPath = href, hrefSearch = ''] = href.split('?');
  if (hrefPath !== SETTINGS_PATH) return isRouteActive(currentPath, hrefPath);
  if (!isRouteActive(currentPath, SETTINGS_PATH)) return false;
  const onDefaultTab = settingsTabFromSearch(currentSearch) === DEFAULT_SETTINGS_TAB;
  const opensDefaultTab = settingsTabFromSearch(hrefSearch) === DEFAULT_SETTINGS_TAB;
  return onDefaultTab === opensDefaultTab;
}
